import { ArrowRight, CircleAlert, Cloud, LockKeyhole, X } from 'lucide-react';

export type ProviderCheckState =
  | { kind: 'idle' }
  | { kind: 'confirming' }
  | { kind: 'running' }
  | { kind: 'complete' }
  | { kind: 'not_configured' }
  | { kind: 'error'; message: string };

interface ProviderConsentProps {
  state: ProviderCheckState;
  availability: 'AVAILABLE' | 'NOT_CONFIGURED' | 'DISABLED' | 'UNAVAILABLE' | 'UNKNOWN';
  onBegin: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ProviderConsent({ state, availability, onBegin, onCancel, onConfirm }: ProviderConsentProps) {
  if (state.kind === 'confirming') {
    return (
      <section className="provider-consent" aria-labelledby="provider-consent-title">
        <div className="provider-consent__heading">
          <span><Cloud size={18} /></span>
          <div><small>Optional external check</small><h2 id="provider-consent-title">Send this file to OpenAI?</h2></div>
          <button type="button" onClick={onCancel} aria-label="Cancel OpenAI check"><X size={18} /></button>
        </div>
        <p><strong>This sends the file to OpenAI for verification.</strong> ORIGNTD first receives the file, then transmits it to the official OpenAI Content Provenance API.</p>
        <dl>
          <div><dt>Purpose</dt><dd>Check OpenAI C2PA and SynthID provenance signals.</dd></div>
          <div><dt>ORIGNTD retention</dt><dd>The source file is not retained by ORIGNTD.</dd></div>
          <div><dt>OpenAI retention</dt><dd>ORIGNTD does not assert OpenAI’s retention behavior.</dd></div>
          <div><dt>Meaning</dt><dd>A provider result is evidence from that provider, not proof of authorship or factual truth.</dd></div>
        </dl>
        <div className="provider-consent__actions">
          <button type="button" className="secondary-action" onClick={onCancel}>Keep local result</button>
          <button type="button" className="primary-action" onClick={onConfirm}>Send file and check <ArrowRight size={16} /></button>
        </div>
      </section>
    );
  }

  return (
    <section className="provider-option" aria-label="Optional OpenAI provenance check" aria-live="polite">
      <div><Cloud size={20} /><span><small>Optional provider signal</small><strong>OpenAI Content Provenance</strong></span></div>
      {state.kind === 'complete' ? (
        <p><LockKeyhole size={15} /> Provider check complete. Execution and retention disclosures are shown below.</p>
      ) : state.kind === 'running' ? (
        <p aria-live="polite"><span className="provider-spinner" /> Checking OpenAI provenance signals…</p>
      ) : state.kind === 'not_configured' || availability === 'NOT_CONFIGURED' ? (
        <p><CircleAlert size={15} /> Not configured on this deployment. No file was sent.</p>
      ) : availability === 'DISABLED' ? (
        <p><CircleAlert size={15} /> Disabled on this deployment. No file was sent.</p>
      ) : availability === 'UNAVAILABLE' ? (
        <p><CircleAlert size={15} /> Provider check unavailable. No file was sent.</p>
      ) : availability === 'UNKNOWN' ? (
        <p><CircleAlert size={15} /> Provider capability could not be confirmed. No file was sent.</p>
      ) : (
        <>
          <p>This does not run automatically. It requires a separate consent because the file will leave your device.</p>
          <button type="button" className="provider-check-button" onClick={onBegin}>Check OpenAI provenance signals <ArrowRight size={15} /></button>
          {state.kind === 'error' && <p className="provider-error" role="alert">{state.message}</p>}
        </>
      )}
    </section>
  );
}
